import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../../redux/store';
import { createGiftGalleryApi, getGiftGalleryByIdApi, updateGiftGalleryApi } from '../../../../../redux/slices/GiftGallerySlice';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

const GiftGalleryForm = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const navigate = useNavigate();

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      name: '',
      required_points: null,
      required_money: null,
      valid_upto: '',
      description: '',
      gift_image: null as File | null, // Ensure the type is File or null
      is_active: 0,
      gift_type: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      gift_type: Yup.string().required('Gift type is required'),
      required_points: Yup.number()
        .when('gift_type', {
          is: (gift_type: string) => gift_type === 'points' || gift_type === 'both',
          then: (schema) => schema.required('Required points are required').min(1, 'Points must be at least 1'),
          otherwise: (schema) => schema.notRequired(),
        }),
      required_money: Yup.number()
        .when('gift_type', {
          is: (gift_type: string) => gift_type === 'money' || gift_type === 'both',
          then: (schema) => schema.required('Required money is required').min(0.01, 'Money must be at least 0.01'),
          otherwise: (schema) => schema.notRequired(),
        }),
      valid_upto: Yup.date()
        .required('Validity date is required')
        .min(new Date(), 'Validity date must be in the future'),
      gift_image: Yup.mixed()
        .required('Gift image is required')
        .test('fileType', 'Only image files are allowed', (value) => {
          if (!value) return false; // Ensure a file is uploaded
          return (value as File).type.startsWith('image/');
        }),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('valid_upto', values.valid_upto);
      formData.append('description', values.description);
      formData.append('is_active', values.is_active.toString());
      formData.append('gift_type', values.gift_type);
      
      // Only include required_points if gift type is 'points' or 'both'
      if (values.gift_type === 'points' || values.gift_type === 'both') {
        formData.append('required_points', values.required_points || '0');
      }
      
      // Only include required_money if gift type is 'money' or 'both'
      if (values.gift_type === 'money' || values.gift_type === 'both') {
        formData.append('required_money', values.required_money || '0.00');
      }
      
      if (values.gift_image) {
        formData.append('gift_image', values.gift_image);
      }

      let data;
      if (id) {
        // Update gift gallery API call
        data = await dispatch(updateGiftGalleryApi({ id, payload: formData }));
      } else {
        // Create new gift gallery API call
        data = await dispatch(createGiftGalleryApi(formData));
      }

      if (data.payload.success) {
        setLoading(false);
        toast.success(data.payload.reason);
      } else {
        setLoading(false);
        toast.error(data.payload.reason ? data.payload.reason : 'Something went wrong');
      }
      navigate(-1);
    },
  });

  useEffect(() => {
    if (id) {
      fetchGiftGalleryData();
    }
  }, [id]);

  // const fetchGiftGalleryData = async () => {
  //   const response = await dispatch(getGiftGalleryByIdApi(id))
  //   if (response.payload.success) {
  //     formik.setValues(response.payload.data?.data)
  //   } else {
  //     toast.error('Failed to fetch gift gallery data')
  //   }
  // }
  const fetchGiftGalleryData = async () => {
    dispatch(getGiftGalleryByIdApi(id)).then((response) => {
      if (response.payload) {
        const fetchedData = response.payload.data?.data || response.payload;
        console.log(fetchedData)
        formik.setValues({
          ...fetchedData,
          gift_image: fetchedData.gift_image_url, // Keep this null to handle file inputs correctly
          required_money: fetchedData.required_money || '', // Ensure required_money is handled
        });

        if (fetchedData.gift_image_url) {
          setImagePreview(fetchedData.gift_image_url);
        } else {
          setImagePreview(null);
        }
      } else {
        toast.error('Failed to fetch gift gallery data');
      }
    }).catch(() => {
      toast.error('An error occurred while fetching data');
    });
  };

  const handleDownload = () => {
    if (imagePreview) {
      fetch(imagePreview)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'gift_image_url.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error('Failed to download image:', error); // Handle fetch errors
        });
    } else {
      console.error('Image preview is not available'); // Handle missing image
    }
  };

  return (
    <div className="container mt-4">
      <div className="card shadow" style={{ height: 'auto', minHeight: '500px' }}>
        <form onSubmit={formik.handleSubmit}>
          {/* Card Header */}
          <div className="card-header bg-light d-flex justify-content-between align-items-center position-relative py-6">
            <h2 className="mb-0">{id ? 'Edit Gift Gallery' : 'Add Gift Gallery'}</h2>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : id ? 'Update' : 'Submit'}
            </button>
            <span
              className={`position-absolute border-primary`}
              style={{
                top: '12px',
                left: '0',
                height: '70%',
                width: '4px',
                backgroundColor: 'var(--bs-primary)',
              }}
            ></span>
          </div>

          {/* Card Body */}
          <div className="card-body p-6">
            <div className="row g-3">
              {/* Name */}
              <div className="col-md-6">
                <label htmlFor="name" className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Gift name"
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="text-danger">{formik.errors.name}</div>
                )}
              </div>

               {/* Gift Type */}
              <div className="col-md-6">
                  <label htmlFor="gift_type" className="form-label me-3">
                    Gift Type <span className="text-danger">*</span>
                  </label>
                  <select
                    id="gift_type"
                    name="gift_type"
                    className="form-select"
                    value={formik.values.gift_type}
                    onChange={(e) => {
                      const selectedType = e.target.value;
                      formik.setFieldValue('gift_type', selectedType);
                      
                      // Clear fields based on selection
                      if (selectedType === 'points') {
                        formik.setFieldValue('required_money', null);
                      } else if (selectedType === 'money') {
                        formik.setFieldValue('required_points', null);
                      } else if (selectedType === 'both') {
                        // Keep both fields for 'both' type
                      } else {
                        // Clear both fields if no type selected
                        formik.setFieldValue('required_points', null);
                        formik.setFieldValue('required_money', null);
                      }
                    }}
                  >
                    <option value="">Select Gift Type</option>
                    <option value="points">Points</option>
                    <option value="money">Money</option>
                    <option value="both">Both</option>
                  </select>
                </div>

              {/* Required Points - Show when gift_type is 'points' or 'both' */}
              {formik.values.gift_type && (formik.values.gift_type === 'points' || formik.values.gift_type === 'both') && (
                <div className="col-md-6">
                  <label htmlFor="required_points" className="form-label">
                    Required Points <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="required_points"
                    name="required_points"
                    className="form-control"
                    value={formik.values.required_points || ''}
                    onChange={(e) => {
                      formik.setFieldValue('required_points', e.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="Enter required points"
                    min="1"
                  />
                  {formik.touched.required_points && formik.errors.required_points && (
                    <div className="text-danger">{formik.errors.required_points}</div>
                  )}
                </div>
              )}

              {/* Required Money - Show when gift_type is 'money' or 'both' */}
              {formik.values.gift_type && (formik.values.gift_type === 'money' || formik.values.gift_type === 'both') && (
                <div className="col-md-6">
                  <label htmlFor="required_money" className="form-label">
                    Required Money <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="required_money"
                    name="required_money"
                    className="form-control"
                    value={formik.values.required_money || ''}
                    onChange={(e) => {
                      formik.setFieldValue('required_money', e.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="Enter required money"
                    step="0.01"
                    min="0.01"
                  />
                  {formik.touched.required_money && formik.errors.required_money && (
                    <div className="text-danger">{formik.errors.required_money}</div>
                  )}
                </div>
              )}

              {/* Valid Upto */}
              <div className="col-md-6">
                <label htmlFor="valid_upto" className="form-label">
                  Valid Upto <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  id="valid_upto"
                  name="valid_upto"
                  className="form-control"
                  value={formik.values.valid_upto}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.valid_upto && formik.errors.valid_upto && (
                  <div className="text-danger">{formik.errors.valid_upto}</div>
                )}
              </div>

              {/* Status */}
              <div className="col-md-6">
                <label htmlFor="status" className="form-label me-3">
                  Status <span className="text-danger">*</span>
                </label>
                <select
                  id="status"
                  name="status"
                  className="form-select"
                  value={formik.values.is_active}
                  onChange={(e) => {
                    const value = e.target.value === '0' ? 0 : 1;
                    formik.setFieldValue('is_active', value);
                  }}
                >
                  <option value={0}>Active</option>
                  <option value={1}>Inactive</option>
                </select>
              </div>

              {/* Gift Image */}
              <div className="col-md-6">
                <label htmlFor="giftImage" className="form-label">
                  Gift Image <span className="text-danger">*</span>
                </label>
                <input
                  type="file"
                  id="giftImage"
                  name="giftImage"
                  className="form-control"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    if (e.target?.files?.[0]) {
                      formik.setFieldValue('gift_image', e.target.files[0]);
                      setImagePreview(URL.createObjectURL(e.target.files[0])); // Update preview
                    }
                  }}
                />
                {formik.touched.gift_image && formik.errors.gift_image && (
                  <div className="text-danger">{formik.errors.gift_image}</div>
                )}
                {imagePreview && (
                  <div className="mt-3">
                    <img src={imagePreview} alt="Preview" className="img-thumbnail" style={{ maxWidth: '200px' }} />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setImagePreview(null);
                        formik.setFieldValue('gift_image', null); // Clear image
                      }}
                    >
                      Remove Image
                    </button>
                    <button
                      type='button'
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      style={{ marginLeft: '10px', marginBottom: '8px' }}
                      onClick={handleDownload}
                    >
                      <span><i className="fa-solid fa-download"></i></span>
                    </button>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="col-md-6">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  placeholder="Enter description"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GiftGalleryForm;
